import React, { useState } from "react";

import { ConsentedPolicyEnum } from "@/core/constants";
import { getConsentPayload } from "@/lib/helpers/getConsentPayload";
import Spinner from "@/components/UI/Spinner";
import type { Task } from "@/core/types";
import { TaskTypeEnum } from "@/core/constants";
import { useAppendConsent } from "@/api2/Consent/mutations/useAppendConsent";
import { useLogInTaskContext } from "@/containers/Dashboard/Patient/LogInTaskContext";
import { usePolicyMetadataQuery } from "@/api2/Consent/queries/usePolicyMetadata";

import { ConsentToACP } from "./components/ConsentToACP";
import { ConsentToPolicy } from "./components/ConsentToPolicy";

export function ConsentRouter() {
  const [keyId, setKeyId] = useState(0);
  const { onTaskCompleted, task } = useLogInTaskContext();
  const { data: policyMetadata, isLoading } = usePolicyMetadataQuery();
  const { mutateAsync: appendConsent } = useAppendConsent();

  const policyType = task?.policy_type;

  const onGiveConsent = async (response: boolean = true) => {
    if (!policyMetadata || !task) return;

    const policy = policyMetadata[policyType];
    const consentPayload = getConsentPayload({
      consentResponse: response,
      consentType: ConsentedPolicyEnum[policyType],
      patientId: task.target_patient_id,
      policyMetadata,
      policyVersion: policy.current_version,
    });

    await appendConsent(consentPayload);

    onTaskCompleted();
    setKeyId((prev) => prev + 1);
  };

  if (isLoading || !policyMetadata || !task) return <Spinner />;

  /* oxlint-disable stylistic/padding-line-between-statements */
  switch (policyType) {
    case "ADVANCED_CARE_PROGRAM":
      return (
        <ConsentToACP
          key={`ConsentToACPKey-${keyId}`}
          onSubmit={onGiveConsent}
          policyMetadata={policyMetadata}
        />
      );

    case "CONSENT_TO_TREATMENT":
    case "HIPAA":
    case "PRIVACY":
    case "TERMS_AND_CONDITIONS":
      return (
        <ConsentToPolicy
          key={`ConsentOnLogInKey-${keyId}`}
          onSubmit={onGiveConsent}
          policyMetadata={policyMetadata}
          subTitle={getConsentCopy(task)}
          title={task.label}
        />
      );

    default:
      return <Spinner />; // better than nothing
  }
  /* oxlint-enable stylistic/padding-line-between-statements */
}

function getConsentCopy(task: Task) {
  const { target_patient_name, task_type } = task;

  switch (task_type) {
    case TaskTypeEnum.UpdatedChildConsent:
      return `We've updated the terms of our policies. By selecting "Agree & Continue", you confirm that you have read and agree to the following policies on behalf of your child ${target_patient_name}.`;

    case TaskTypeEnum.UpdatedConsentRequired:
      return 'Our records indicate that you recently turned 18. By selecting "Agree & Continue", you confirm that you have read and agree to the following policies.';

    case TaskTypeEnum.UpdatedPolicies:
      return 'We\'ve updated the terms of our policies. By selecting "Agree & Continue", you confirm that you have read and agree to the following policies.';

    default:
      return "";
  }
}
